/**
 * ComparePanel
 *
 * Side-by-side comparison of 2–3 portfolio clients.
 * Full-screen on mobile (horizontal scroll with sticky label column),
 * large right-drawer on desktop (max-w-6xl).
 *
 * Delta highlighting: best score → teal up-arrow tag, worst → coral down-arrow tag.
 * Values within 5 points of each other are treated as tied (neutral).
 */

import { usePortfolioLocks } from '@/hooks/usePortfolioLocks';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { lifecycleStageLabel, healthSignalLabel, tierLabel, type LabelT } from '@/utils/portfolioLabels';
import { useTimezone } from '@/contexts/TimezoneContext';
import { Tooltip } from '@/components/ui/Tooltip';
import { GlossaryTooltip } from './GlossaryTooltip';
import type { PortfolioClient, HealthTier, HealthDimension } from '@/data/portfolio.types';
import { avatarTextClass } from '@/utils/avatarContrast';
import { lockBodyScroll } from '@/hooks/useBodyScrollLock';

// ── Types ──────────────────────────────────────────────────────────────────────

interface ComparePanelProps {
  clients: PortfolioClient[];
  isOpen: boolean;
  onClose: () => void;
}

interface DeltaResult {
  winnerId: number | null;
  loserId: number | null;
  tied: boolean;
}

// ── Tier helpers (duplicated from parent to keep component self-contained) ─────

const TIER_CONFIG: Record<HealthTier, { dotColor: string; pillBg: string; pillText: string }> = {
  on_track: { dotColor: 'bg-primary-500', pillBg: 'bg-primary-50', pillText: 'text-primary-700' },
  watch:    { dotColor: 'bg-peach',       pillBg: 'bg-peach/30',  pillText: 'text-coral-700'  },
  at_risk:  { dotColor: 'bg-coral-400',   pillBg: 'bg-coral-50',   pillText: 'text-coral-700'   },
  off_track:{ dotColor: 'bg-coral-600',   pillBg: 'bg-coral-50',     pillText: 'text-coral-700'     },
  paused:   { dotColor: 'bg-sage-gray',   pillBg: 'bg-gray-100',   pillText: 'text-sage-gray'   },
};

const LIFECYCLE_CONFIG: Record<string, { bg: string; text: string }> = {
  active:      { bg: 'bg-primary-50', text: 'text-primary-700' },
  onboarding:  { bg: 'bg-peach/30',  text: 'text-coral-700'  },
  churning:    { bg: 'bg-coral-50',   text: 'text-coral-700'   },
  paused:      { bg: 'bg-gray-100',   text: 'text-sage-gray'   },
};

function scoreToTier(score: number): HealthTier {
  if (score >= 80) return 'on_track';
  if (score >= 60) return 'watch';
  if (score >= 40) return 'at_risk';
  if (score > 0)   return 'off_track';
  return 'paused';
}

// ── Delta computation ──────────────────────────────────────────────────────────

/**
 * Given a list of { id, value } pairs, determine which id has the best/worst value.
 * If the spread between max and min is <= TIE_THRESHOLD, all are considered tied.
 * If best and second-best are within TIE_THRESHOLD, no winner is declared.
 */
const TIE_THRESHOLD = 5;

function computeDelta(entries: Array<{ id: number; value: number }>): DeltaResult {
  if (entries.length < 2) return { winnerId: null, loserId: null, tied: true };

  const values = entries.map((e) => e.value);
  const max = Math.max(...values);
  const min = Math.min(...values);

  if (max - min <= TIE_THRESHOLD) {
    return { winnerId: null, loserId: null, tied: true };
  }

  // Check if the best is clearly ahead of second-best
  const sorted = [...entries].sort((a, b) => b.value - a.value);
  const winner = sorted[0];
  const loser = sorted[sorted.length - 1];
  const secondBest = sorted[1];

  // If top two are close, no clear winner
  const winnerId = winner.value - secondBest.value > TIE_THRESHOLD ? winner.id : null;
  // Loser is always marked if spread is large enough
  const loserId = loser.id;

  return { winnerId, loserId, tied: false };
}

/** Localized pipeline cover text: Empty / 5d / 14d+ */
function coverLabel(t: LabelT, coverDays: number): string {
  if (coverDays <= 0) return t('compare_empty', { defaultValue: 'Empty' });
  if (coverDays >= 14) return t('compare_days_max', { defaultValue: '14d+' });
  return t('compare_days', { n: coverDays, defaultValue: '{{n}}d' });
}

/** Localized velocity trend text: flat / +12% / -8% */
function trendLabel(t: LabelT, trendPct: number): string {
  if (trendPct === 0) return t('compare_trend_flat', { defaultValue: 'flat' });
  return trendPct > 0 ? `+${Math.round(trendPct)}%` : `${Math.round(trendPct)}%`;
}

// ── Small sub-components ───────────────────────────────────────────────────────

const DeltaBadge = ({ type }: { type: 'best' | 'worst' | 'neutral' }) => {
  const { t } = useTranslation('agency');
  if (type === 'best') {
    return (
      <GlossaryTooltip termId="delta-best" side="top" frameShape="pill">
        <span
          className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-primary-600 ring-1 ring-primary-300 px-1.5 py-0.5 rounded-full leading-none cursor-default"
          aria-label={t('compare_delta_best_aria', { defaultValue: 'Best' })}
        >
          <i className="hm hm-arrow-up" style={{ fontSize: 9 }} aria-hidden="true" />
          {t('compare_delta_best', { defaultValue: 'Best' })}
        </span>
      </GlossaryTooltip>
    );
  }
  if (type === 'worst') {
    return (
      <GlossaryTooltip termId="delta-needs" side="top" frameShape="pill">
        <span
          className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-coral-600 ring-1 ring-coral-200 px-1.5 py-0.5 rounded-full leading-none cursor-default"
          aria-label={t('compare_delta_needs_aria', { defaultValue: 'Needs attention' })}
        >
          <i className="hm hm-arrow-down" style={{ fontSize: 9 }} aria-hidden="true" />
          {t('compare_delta_needs', { defaultValue: 'Needs' })}
        </span>
      </GlossaryTooltip>
    );
  }
  return null;
};

/** Stacked mini progress bars for a health dimension's signals */
const DimensionBreakdown = ({
  dimension,
  deltaType,
}: {
  dimension: HealthDimension;
  deltaType: 'best' | 'worst' | 'neutral';
}) => {
  const { t } = useTranslation('agency');
  const overallTier = scoreToTier(dimension.score);
  const cfg = TIER_CONFIG[overallTier];

  return (
    <div className="space-y-1.5">
      {dimension.signals.map((sig) => {
        const sigTier = scoreToTier(sig.score);
        const sigCfg = TIER_CONFIG[sigTier];
        return (
          <div key={sig.label}>
            <div className="flex items-center justify-between mb-0.5">
              <span className="text-[11px] text-gray-500 leading-none">{healthSignalLabel(t, sig)}</span>
              <span className={`text-[11px] font-semibold ${sigCfg.pillText}`}>{sig.score}</span>
            </div>
            <div className="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full ${sigCfg.dotColor} transition-all`}
                style={{ width: `${sig.score}%` }}
              />
            </div>
          </div>
        );
      })}
      {/* Overall row */}
      <div className="pt-1 border-t border-gray-100 flex items-center justify-between">
        <span className="text-[11px] font-semibold text-gray-600">{t('compare_overall', { defaultValue: 'Overall' })}</span>
        <div className="flex items-center gap-1.5">
          <span className={`text-[11px] font-bold ${cfg.pillText}`}>{dimension.score}</span>
          <DeltaBadge type={deltaType} />
        </div>
      </div>
    </div>
  );
};

/** Pending-approval count display with color coding. Carte Blanche clients have
 *  no approval workflow (POST-STATUS-MODEL.md) — render N/A instead of a count
 *  so this stays consistent with the CB gating used elsewhere in the portfolio. */
const PendingDisplay = ({
  count,
  deltaType,
  carteBlanche,
}: {
  count: number;
  deltaType: 'best' | 'worst' | 'neutral';
  carteBlanche: boolean;
}) => {
  const { t } = useTranslation('agency');
  if (carteBlanche) {
    return <span className="text-xs text-gray-400 italic">{t('compare_na', { defaultValue: 'N/A' })}</span>;
  }

  // Lower is better for pending - delta is inverted (lower = best)
  const color =
    count === 0
      ? 'text-primary-600 font-bold'
      : count <= 3
      ? 'text-coral-700 font-semibold'
      : 'text-coral-600 font-bold';

  return (
    <div className="flex flex-col items-center gap-1">
      <span className={`text-xl tabular-nums ${color}`}>{count}</span>
      <DeltaBadge type={deltaType} />
    </div>
  );
};

/** Pipeline display: days of planned + ready content ahead (server content cover) */
const RunwayDisplay = ({
  coverDays,
  isPaused,
  deltaType,
}: {
  coverDays: number;
  isPaused: boolean;
  deltaType: 'best' | 'worst' | 'neutral';
}) => {
  const { t } = useTranslation('agency');
  if (isPaused) {
    return <span className="text-xs text-gray-400 italic">{tierLabel(t, 'paused')}</span>;
  }

  const text = coverLabel(t, coverDays);

  const color =
    coverDays <= 0
      ? 'text-coral-600 font-semibold'
      : coverDays >= 14
      ? 'text-primary-600 font-semibold'
      : coverDays >= 7
      ? 'text-coral-700 font-medium'
      : 'text-coral-500 font-medium';

  return (
    <div className="flex flex-col items-center gap-1">
      <span className={`text-sm tabular-nums ${color}`}>{text}</span>
      <DeltaBadge type={deltaType} />
    </div>
  );
};

/** Velocity display: this week count + trend % vs prior week */
const VelocityDisplay = ({
  thisWeek,
  trendPct,
  deltaType,
}: {
  thisWeek: number;
  trendPct: number;
  deltaType: 'best' | 'worst' | 'neutral';
}) => {
  const { t } = useTranslation('agency');
  const trendText = trendLabel(t, trendPct);
  const trendColor =
    trendPct > 0
      ? 'text-primary-600'
      : trendPct < 0
      ? 'text-coral-600'
      : 'text-gray-400';
  return (
    <div className="flex flex-col items-center gap-1">
      <div className="flex items-baseline gap-1">
        <span className="text-xl font-semibold tabular-nums text-gray-900">{thisWeek}</span>
        <span className="text-[10px] uppercase tracking-wide text-gray-400">{t('compare_per_week', { defaultValue: '/wk' })}</span>
      </div>
      <span className={`text-[10px] font-medium tabular-nums ${trendColor}`}>{trendText}</span>
      <DeltaBadge type={deltaType} />
    </div>
  );
};

/** Cadence attainment: postsThisWeek vs cadenceWeekly target as % */
const CadenceDisplay = ({
  thisWeek,
  target,
  deltaType,
}: {
  thisWeek: number;
  target: number;
  deltaType: 'best' | 'worst' | 'neutral';
}) => {
  const { t } = useTranslation('agency');
  if (target <= 0) {
    return <span className="text-xs text-gray-400 italic">{t('compare_no_target', { defaultValue: 'No target' })}</span>;
  }
  const pct = Math.round((thisWeek / target) * 100);
  const color =
    pct >= 100
      ? 'text-primary-600 font-bold'
      : pct >= 70
      ? 'text-primary-700 font-semibold'
      : pct >= 40
      ? 'text-coral-700 font-medium'
      : 'text-coral-600 font-semibold';
  return (
    <div className="flex flex-col items-center gap-1">
      <div className="flex items-baseline gap-1">
        <span className={`text-xl tabular-nums ${color}`}>{thisWeek}</span>
        <span className="text-xs text-gray-400">/{target}</span>
      </div>
      <span className="text-[10px] tabular-nums text-gray-500">{pct}%</span>
      <DeltaBadge type={deltaType} />
    </div>
  );
};

/** Renewal: days until renewal + at-risk flag */
const RenewalDisplay = ({
  daysUntilRenewal,
  atRisk,
  deltaType,
}: {
  daysUntilRenewal: number | null;
  atRisk: boolean;
  deltaType: 'best' | 'worst' | 'neutral';
}) => {
  const { t } = useTranslation('agency');
  if (daysUntilRenewal === null) {
    return <span className="text-xs text-gray-400 italic">—</span>;
  }
  const text =
    daysUntilRenewal <= 0
      ? t('compare_overdue', { n: Math.abs(daysUntilRenewal), defaultValue: '{{n}}d overdue' })
      : t('compare_days', { n: daysUntilRenewal, defaultValue: '{{n}}d' });
  const color =
    atRisk || daysUntilRenewal <= 0
      ? 'text-coral-600 font-semibold'
      : daysUntilRenewal <= 30
      ? 'text-coral-700 font-medium'
      : 'text-primary-600 font-semibold';
  return (
    <div className="flex flex-col items-center gap-1">
      <span className={`text-sm tabular-nums ${color}`}>{text}</span>
      {atRisk && (
        <span className="text-[10px] uppercase tracking-wide text-coral-600 font-bold">{tierLabel(t, 'at_risk')}</span>
      )}
      <DeltaBadge type={deltaType} />
    </div>
  );
};

/** Channel health: broken / expiring counts */
const ChannelHealthDisplay = ({
  total,
  broken,
  expiring,
  deltaType,
}: {
  total: number;
  broken: number;
  expiring: number;
  deltaType: 'best' | 'worst' | 'neutral';
}) => {
  const { t } = useTranslation('agency');
  const issues = broken + expiring;
  return (
    <div className="flex flex-col items-center gap-1">
      <div className="flex items-baseline gap-1">
        <span className="text-xl font-semibold tabular-nums text-gray-900">{total}</span>
        <span className="text-[10px] uppercase tracking-wide text-gray-400">{t('compare_channels_unit', { defaultValue: 'channels' })}</span>
      </div>
      {issues > 0 ? (
        <span className="inline-flex items-center gap-1 text-[10px] font-medium">
          {broken > 0 && <span className="text-coral-600">{t('compare_broken', { n: broken, defaultValue: '{{n}} broken' })}</span>}
          {broken > 0 && expiring > 0 && <span className="text-gray-300">·</span>}
          {expiring > 0 && <span className="text-coral-700">{t('compare_expiring', { n: expiring, defaultValue: '{{n}} expiring' })}</span>}
        </span>
      ) : (
        <span className="text-[10px] font-medium text-primary-600">{t('compare_all_healthy', { defaultValue: 'All healthy' })}</span>
      )}
      <DeltaBadge type={deltaType} />
    </div>
  );
};

/** Owner / brand-lead chip */
const OwnerChip = ({
  initials,
  name,
  color,
  profilePhotoUrl,
}: {
  initials: string | null;
  name: string | null;
  color: string | null;
  profilePhotoUrl?: string | null;
}) => {
  const { t } = useTranslation('agency');
  if (!name) {
    return <span className="text-xs text-gray-400 italic">{t('compare_unassigned', { defaultValue: 'Unassigned' })}</span>;
  }
  return (
    <div className="flex items-center gap-2">
      <span
        className={`w-6 h-6 rounded-full inline-flex items-center justify-center text-[9px] font-bold shrink-0 ring-1 ring-gray-200 overflow-hidden ${profilePhotoUrl ? '' : `${color ?? 'bg-gray-400'} ${avatarTextClass(color)}`}`}
        aria-hidden="true"
      >
        {profilePhotoUrl ? (
          <img
            src={profilePhotoUrl}
            alt=""
            className="w-full h-full rounded-full object-cover"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
        ) : (
          initials
        )}
      </span>
      <span className="text-xs font-medium text-gray-700 truncate">{name}</span>
    </div>
  );
};

/**
 * Mobile-only section card. Renders the section label as a Wagner overline
 * and one row per client (avatar + name + render-prop value) stacked
 * vertically. Avoids the per-section horizontal scrolls that made the
 * compare panel painful at narrow widths. Use this side-by-side with the
 * desktop `hidden sm:grid` block — this one carries `sm:hidden`.
 */
const MobileSection = ({
  label,
  clients,
  render,
  className = '',
}: {
  label: string;
  clients: PortfolioClient[];
  render: (client: PortfolioClient) => React.ReactNode;
  className?: string;
}) => (
  <section className={`sm:hidden border-b border-gray-100 ${className}`}>
    <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-[0.15em] px-4 pt-3.5 pb-2">
      {label}
    </p>
    <div className="px-4 pb-3.5 divide-y divide-gray-50">
      {clients.map((client) => (
        <div key={client.id} className="flex items-center gap-2.5 py-2 first:pt-0 last:pb-0">
          <div
            className={`w-7 h-7 rounded-md flex items-center justify-center text-[11px] font-bold ${avatarTextClass(client.avatarColor)} shrink-0 ${client.avatarColor}`}
            aria-hidden="true"
          >
            {client.avatarInitial}
          </div>
          <span className="text-sm font-medium text-gray-700 flex-1 truncate min-w-0">{client.name}</span>
          <div className="shrink-0 flex items-center gap-2">{render(client)}</div>
        </div>
      ))}
    </div>
  </section>
);

// ── Desktop section header row - editorial overline + hairline ────────────────

const DesktopSectionHeader = ({
  label,
  tooltipKey,
  tooltipDefault,
  glossaryTermId,
}: {
  label: string;
  tooltipKey: string;
  tooltipDefault: string;
  glossaryTermId?: string;
}) => {
  const { t } = useTranslation('agency');
  return (
    <div className="hidden sm:block px-6 pt-4 pb-2 bg-white border-t border-gray-900/10 sticky top-[52px] z-10">
      <div className="flex items-center gap-1.5">
        <Tooltip content={t(tooltipKey, { defaultValue: tooltipDefault })} side="right" delayDuration={300}>
          <span className="text-[11px] font-medium uppercase tracking-[0.2em] text-gray-500 cursor-default">{label}</span>
        </Tooltip>
        {glossaryTermId && (
          <GlossaryTooltip termId={glossaryTermId} side="right" frameShape="pill">
            <button
              type="button"
              className="inline-flex items-center justify-center w-4 h-4 rounded-full text-gray-300 hover:text-gray-500 transition-colors focus:outline-none border border-transparent focus-visible:border-primary-600"
              aria-label={t('compare_learn_more', { label, defaultValue: 'Learn more about {{label}}' })}
            >
              <i className="hm hm-info" style={{ fontSize: 12 }} aria-hidden="true" />
            </button>
          </GlossaryTooltip>
        )}
      </div>
    </div>
  );
};

// ── Main ComparePanel ──────────────────────────────────────────────────────────

export const ComparePanel = ({ clients, isOpen, onClose }: ComparePanelProps) => {
  const { t } = useTranslation('agency');
  const { isLocked } = usePortfolioLocks();
  const healthLocked = isLocked('portfolio_health_score');
  const runwayLocked = isLocked('portfolio_runway');
  const velocityLocked = isLocked('portfolio_velocity');
  const cadenceLocked = isLocked('portfolio_cadence');
  const relationshipLocked = isLocked('portfolio_client_relationship');
  const channelHealthLocked = isLocked('portfolio_channel_health');
  const { formatRelativeDate } = useTimezone();
  const panelRef = useRef<HTMLDivElement>(null);

  // Trap focus + escape close
  useEffect(() => {
    if (!isOpen) return;

    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !e.defaultPrevented) onClose();
    };
    document.addEventListener('keydown', handleKey);
    const unlockScroll = lockBodyScroll();

    return () => {
      document.removeEventListener('keydown', handleKey);
      unlockScroll();
    };
  }, [isOpen, onClose]);

  if (!isOpen || clients.length < 2) return null;

  // ── Pre-compute deltas per metric ──────────────────────────────────────────

  const opDelta = computeDelta(clients.map((c) => ({ id: c.id, value: c.healthOperational.score })));
  const beDelta = computeDelta(clients.map((c) => ({ id: c.id, value: c.healthBrandEquity.score })));
  const comDelta = computeDelta(clients.map((c) => ({ id: c.id, value: c.healthCommercial.score })));
  // For pipeline (content cover), higher is better
  const runDelta = computeDelta(clients.map((c) => ({ id: c.id, value: c.coverDays })));
  // Pending Approvals = posts genuinely awaiting client sign-off, i.e.
  // pipeline.awaitingApproval — NOT pendingCount (which is UNSCHEDULED
  // drafts, see WorkloadView.tsx for the sibling fix). Carte Blanche clients
  // have no approval workflow (POST-STATUS-MODEL.md), so they're excluded
  // from ranking here and rendered as N/A below. Lower is better - invert
  // by using negative.
  const pendDelta = computeDelta(
    clients
      .filter((c) => !c.pipeline.carteBlanche)
      .map((c) => ({ id: c.id, value: -(c.pipeline.awaitingApproval ?? 0) })),
  );
  // Velocity: higher posts this week is better
  const velDelta = computeDelta(clients.map((c) => ({ id: c.id, value: c.velocity?.postsThisWeek ?? 0 })));
  // Cadence attainment: higher % is better (ratio of postsThisWeek / cadenceWeekly, capped sensibly)
  const cadDelta = computeDelta(
    clients.map((c) => ({
      id: c.id,
      value: c.cadenceWeekly > 0 ? (c.velocity?.postsThisWeek ?? 0) / c.cadenceWeekly : 0,
    })),
  );
  // Renewal: more days = better. null treated as 0 so tied/neutral.
  const renDelta = computeDelta(
    clients.map((c) => ({ id: c.id, value: c.renewal?.daysUntilRenewal ?? 0 })),
  );
  // Channel health: fewer broken/expiring is better — invert
  const chDelta = computeDelta(
    clients.map((c) => ({
      id: c.id,
      value: -((c.channelHealth?.broken ?? 0) + (c.channelHealth?.expiring ?? 0)),
    })),
  );

  function getDeltaType(delta: DeltaResult, clientId: number): 'best' | 'worst' | 'neutral' {
    if (delta.tied) return 'neutral';
    if (delta.winnerId === clientId) return 'best';
    if (delta.loserId === clientId) return 'worst';
    return 'neutral';
  }

  // ── Column grid classes ────────────────────────────────────────────────────

  const colCount = clients.length;
  const gridCols =
    colCount === 2 ? 'grid-cols-2' : 'grid-cols-3';

  return (
    <div
      className="fixed left-0 right-0 top-0 bottom-[calc(var(--bottom-nav-h)+env(safe-area-inset-bottom))] lg:inset-0 z-50 flex items-end sm:items-stretch sm:justify-end"
      role="dialog"
      aria-modal="true"
      aria-label={t('compare_panel_title', { defaultValue: 'Compare clients' })}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-[2px]"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Panel */}
      <div
        ref={panelRef}
        className="relative bg-white w-full sm:w-auto sm:max-w-6xl sm:min-w-[640px] h-full flex flex-col rounded-t-2xl sm:rounded-none shadow-2xl overflow-hidden"
        style={{ maxWidth: colCount === 3 ? '72rem' : '52rem' }}
      >
        {/* Mobile drag handle */}
        <div className="flex justify-center pt-2.5 pb-1 sm:hidden">
          <div className="w-8 h-1 rounded-full bg-gray-300" />
        </div>

        {/* ── Header ─────────────────────────────────────────────────────── */}
        <div className="flex items-center gap-3 px-4 sm:px-6 py-3 border-b border-gray-900/10 bg-white shrink-0">
          <div className="flex items-center gap-2">
            <h2 className="font-wagner text-2xl leading-none tracking-tight text-gray-900">
              {t('compare_panel_title', { defaultValue: 'Compare' })}
            </h2>
          </div>
          <span className="inline-flex items-center justify-center min-w-[22px] h-5 px-1.5 rounded-full text-xs font-bold bg-gray-100 text-gray-600 tabular-nums">
            {clients.length}
          </span>
          <button
            type="button"
            onClick={onClose}
            className="ml-auto flex items-center justify-center w-8 h-8 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors focus:outline-none border border-transparent focus-visible:border-primary-600"
            aria-label={t('compare_panel_close', { defaultValue: 'Close compare panel' })}
          >
            <i className="hm hm-cross" style={{ fontSize: 16 }} aria-hidden="true" />
          </button>
        </div>

        {/* ── Scrollable body ─────────────────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden">

          {/* ── SECTION: Client headers ── */}

          {/* Desktop: standard grid */}
          <div className={`hidden sm:grid ${gridCols} gap-0 border-b border-gray-200 bg-white sticky top-0 z-20`}>
            {clients.map((client) => {
              const tier = TIER_CONFIG[client.healthOverall];
              const lifecycle = LIFECYCLE_CONFIG[client.lifecycleStage];
              return (
                <div
                  key={client.id}
                  className="px-5 py-4 border-r border-gray-100 last:border-r-0"
                >
                  <div className="flex items-center gap-2.5 mb-2">
                    <div
                      className={`w-9 h-9 rounded-xl flex items-center justify-center text-sm font-bold ${avatarTextClass(client.avatarColor)} shrink-0 ${client.avatarColor}`}
                      aria-hidden="true"
                    >
                      {client.avatarInitial}
                    </div>
                    <div className="min-w-0">
                      <div className="font-semibold text-gray-900 text-sm leading-tight truncate">
                        {client.name}
                      </div>
                      <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${lifecycle.bg} ${lifecycle.text}`}>
                        {lifecycleStageLabel(t, client.lifecycleStage)}
                      </span>
                    </div>
                  </div>
                  {/* Owner */}
                  <div className="flex items-center gap-1.5">
                    <div
                      className={`w-6 h-6 rounded-full inline-flex items-center justify-center text-[9px] font-bold leading-none tracking-[0.02em] shrink-0 overflow-hidden ${client.ownerProfilePhotoUrl ? '' : `${client.ownerAvatarColor} ${avatarTextClass(client.ownerAvatarColor)}`}`}
                      aria-hidden="true"
                    >
                      {client.ownerProfilePhotoUrl ? (
                        <img
                          src={client.ownerProfilePhotoUrl}
                          alt=""
                          className="w-full h-full rounded-full object-cover"
                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        />
                      ) : (
                        <span className="translate-y-px">{client.ownerInitials}</span>
                      )}
                    </div>
                    <span className="text-xs text-gray-500 truncate">{client.ownerName}</span>
                  </div>
                  {/* Overall tier */}
                  {!healthLocked && (
                    <div className="mt-2">
                      <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-semibold ${tier.pillBg} ${tier.pillText}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${tier.dotColor}`} aria-hidden="true" />
                        {tierLabel(t, client.healthOverall)}
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Mobile: clean card-based layout — one stacked section per metric.
               No more nested horizontal scrolls; everything reads top-to-bottom. */}
          <section className="sm:hidden border-b border-gray-100">
            <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-[0.15em] px-4 pt-3.5 pb-2">
              {t('compare_section_clients', { defaultValue: 'Clients' })}
            </p>
            <div className="px-4 pb-3.5 divide-y divide-gray-50">
              {clients.map((client) => {
                const tier = TIER_CONFIG[client.healthOverall];
                const lifecycle = LIFECYCLE_CONFIG[client.lifecycleStage];
                return (
                  <div key={client.id} className="flex items-center gap-2.5 py-2.5 first:pt-0 last:pb-0">
                    <div
                      className={`w-9 h-9 rounded-lg flex items-center justify-center text-sm font-bold ${avatarTextClass(client.avatarColor)} shrink-0 ${client.avatarColor}`}
                      aria-hidden="true"
                    >
                      {client.avatarInitial}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate leading-tight">
                        {client.name}
                      </p>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <span
                          className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${lifecycle.bg} ${lifecycle.text}`}
                        >
                          {lifecycleStageLabel(t, client.lifecycleStage)}
                        </span>
                        {client.ownerName && (
                          <span className="text-[10px] text-gray-400 truncate">· {client.ownerName}</span>
                        )}
                      </div>
                    </div>
                    {!healthLocked && (
                      <span
                        className={`text-[10px] px-2 py-0.5 rounded-full font-semibold leading-none shrink-0 ${tier.pillBg} ${tier.pillText}`}
                      >
                        {tierLabel(t, client.healthOverall)}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </section>

          {!healthLocked && (
          <>
          {/* ── SECTION: Health overview ── */}
          <DesktopSectionHeader
            label={t('compare_section_health', { defaultValue: 'Health Overview' })}
            tooltipKey="compare_tooltip_section_health"
            tooltipDefault="Three dimensions of client health, side by side."
          />

          {/* Desktop */}
          <div className={`hidden sm:grid ${gridCols} gap-0 border-b border-gray-100`}>
            {clients.map((client) => (
              <div key={client.id} className="px-5 py-4 border-r border-gray-100 last:border-r-0 space-y-2">
                <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-2">
                  {t('compare_section_health', { defaultValue: 'Health' })}
                </p>
                <div className="flex flex-col gap-2">
                  {[
                    {
                      label: t('portfolio_health_operational', { defaultValue: 'Operational' }),
                      score: client.healthOperational.score,
                      delta: getDeltaType(opDelta, client.id),
                    },
                    {
                      label: t('portfolio_health_brand_equity', { defaultValue: 'Brand Equity' }),
                      score: client.healthBrandEquity.score,
                      delta: getDeltaType(beDelta, client.id),
                    },
                    {
                      label: t('portfolio_health_commercial', { defaultValue: 'Commercial' }),
                      score: client.healthCommercial.score,
                      delta: getDeltaType(comDelta, client.id),
                    },
                  ].map(({ label, score, delta }) => {
                    const tier = scoreToTier(score);
                    const cfg = TIER_CONFIG[tier];
                    return (
                      <div key={label} className={`px-2.5 py-2 rounded-lg ${cfg.pillBg} flex items-center justify-between gap-2`}>
                        <span className={`text-xs font-medium ${cfg.pillText} truncate`}>{label}</span>
                        <div className="flex items-center gap-1.5 shrink-0">
                          <span className={`text-sm font-bold ${cfg.pillText}`}>{score}</span>
                          <DeltaBadge type={delta} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* Mobile health overview — three score chips per client */}
          <MobileSection
            label={t('compare_section_health', { defaultValue: 'Health' })}
            clients={clients}
            render={(client) => {
              const dims = [
                { key: 'op', score: client.healthOperational.score, delta: getDeltaType(opDelta, client.id) },
                { key: 'be', score: client.healthBrandEquity.score, delta: getDeltaType(beDelta, client.id) },
                { key: 'co', score: client.healthCommercial.score, delta: getDeltaType(comDelta, client.id) },
              ];
              return (
                <>
                  {dims.map(({ key, score, delta }) => {
                    const tier = scoreToTier(score);
                    const cfg = TIER_CONFIG[tier];
                    return (
                      <span
                        key={key}
                        className={`inline-flex items-center gap-0.5 text-[11px] font-bold leading-none px-1.5 h-5 rounded-md ${cfg.pillBg} ${cfg.pillText}`}
                      >
                        {score}
                        {delta === 'best' && <i className="hm hm-arrow-up" style={{ fontSize: 8 }} aria-hidden="true" />}
                        {delta === 'worst' && <i className="hm hm-arrow-down" style={{ fontSize: 8 }} aria-hidden="true" />}
                      </span>
                    );
                  })}
                </>
              );
            }}
          />

          {/* ── SECTION: Operational Breakdown ── */}
          <DesktopSectionHeader
            label={t('compare_section_operational', { defaultValue: 'Operational Breakdown' })}
            tooltipKey="compare_tooltip_section_operational"
            tooltipDefault="Delivery consistency, cadence, and release velocity."
            glossaryTermId="health-operational"
          />

          <div className={`hidden sm:grid ${gridCols} gap-0 border-b border-gray-100`}>
            {clients.map((client) => (
              <div key={client.id} className="px-5 py-4 border-r border-gray-100 last:border-r-0">
                <DimensionBreakdown
                  dimension={client.healthOperational}
                  deltaType={getDeltaType(opDelta, client.id)}
                />
              </div>
            ))}
          </div>

          <MobileSection
            label={t('compare_section_operational', { defaultValue: 'Operational' })}
            clients={clients}
            render={(client) => (
              <>
                <span className="text-sm font-bold tabular-nums text-gray-800">{client.healthOperational.score}</span>
                <DeltaBadge type={getDeltaType(opDelta, client.id)} />
              </>
            )}
          />

          {/* ── SECTION: Brand Equity Breakdown ── */}
          <DesktopSectionHeader
            label={t('compare_section_brand', { defaultValue: 'Brand Equity Breakdown' })}
            tooltipKey="compare_tooltip_section_brand"
            tooltipDefault="Audience growth, engagement quality, and brand visibility signals."
            glossaryTermId="health-brand"
          />

          <div className={`hidden sm:grid ${gridCols} gap-0 border-b border-gray-100`}>
            {clients.map((client) => (
              <div key={client.id} className="px-5 py-4 border-r border-gray-100 last:border-r-0">
                <DimensionBreakdown
                  dimension={client.healthBrandEquity}
                  deltaType={getDeltaType(beDelta, client.id)}
                />
              </div>
            ))}
          </div>

          <MobileSection
            label={t('compare_section_brand', { defaultValue: 'Brand' })}
            clients={clients}
            render={(client) => (
              <>
                <span className="text-sm font-bold tabular-nums text-gray-800">{client.healthBrandEquity.score}</span>
                <DeltaBadge type={getDeltaType(beDelta, client.id)} />
              </>
            )}
          />

          {/* ── SECTION: Commercial Breakdown ── */}
          <DesktopSectionHeader
            label={t('compare_section_commercial', { defaultValue: 'Commercial Breakdown' })}
            tooltipKey="compare_tooltip_section_commercial"
            tooltipDefault="Revenue-linked indicators: leads, referrals, and conversion signals."
            glossaryTermId="health-commercial"
          />

          <div className={`hidden sm:grid ${gridCols} gap-0 border-b border-gray-100`}>
            {clients.map((client) => (
              <div key={client.id} className="px-5 py-4 border-r border-gray-100 last:border-r-0">
                <DimensionBreakdown
                  dimension={client.healthCommercial}
                  deltaType={getDeltaType(comDelta, client.id)}
                />
              </div>
            ))}
          </div>

          <MobileSection
            label={t('compare_section_commercial', { defaultValue: 'Commercial' })}
            clients={clients}
            render={(client) => (
              <>
                <span className="text-sm font-bold tabular-nums text-gray-800">{client.healthCommercial.score}</span>
                <DeltaBadge type={getDeltaType(comDelta, client.id)} />
              </>
            )}
          />
          </>
          )}

          {!runwayLocked && (
          <>
          {/* ── SECTION: Runway ── */}
          <DesktopSectionHeader
            label={t('compare_section_runway', { defaultValue: 'Pipeline' })}
            tooltipKey="compare_tooltip_section_runway"
            tooltipDefault="Days of planned and ready content ahead. Higher = more buffer before the content runs dry."
          />

          <div className={`hidden sm:grid ${gridCols} gap-0 border-b border-gray-100`}>
            {clients.map((client) => (
              <div key={client.id} className="px-5 py-5 border-r border-gray-100 last:border-r-0 flex items-center justify-center">
                <RunwayDisplay
                  coverDays={client.coverDays}
                  isPaused={client.healthOverall === 'paused'}
                  deltaType={getDeltaType(runDelta, client.id)}
                />
              </div>
            ))}
          </div>

          <MobileSection
            label={t('compare_section_runway', { defaultValue: 'Pipeline' })}
            clients={clients}
            render={(client) => {
              if (client.healthOverall === 'paused') {
                return <span className="text-xs text-gray-400 italic">{tierLabel(t, 'paused')}</span>;
              }
              const r = client.coverDays;
              const text = coverLabel(t, r);
              const color = r <= 0 ? 'text-coral-600' : r >= 14 ? 'text-primary-600' : 'text-coral-700';
              return (
                <>
                  <span className={`text-sm font-bold tabular-nums ${color}`}>{text}</span>
                  <DeltaBadge type={getDeltaType(runDelta, client.id)} />
                </>
              );
            }}
          />
          </>
          )}

          {/* ── SECTION: Pending ── */}
          <DesktopSectionHeader
            label={t('compare_section_pending', { defaultValue: 'Pending' })}
            tooltipKey="compare_tooltip_section_pending"
            tooltipDefault="Posts waiting for a release. Lower is better - high counts stall scheduling."
          />

          <div className={`hidden sm:grid ${gridCols} gap-0 border-b border-gray-100`}>
            {clients.map((client) => (
              <div key={client.id} className="px-5 py-5 border-r border-gray-100 last:border-r-0 flex items-center justify-center">
                <PendingDisplay
                  count={client.pipeline.awaitingApproval ?? 0}
                  deltaType={getDeltaType(pendDelta, client.id)}
                  carteBlanche={!!client.pipeline.carteBlanche}
                />
              </div>
            ))}
          </div>

          <MobileSection
            label={t('compare_section_pending', { defaultValue: 'Pending' })}
            clients={clients}
            render={(client) => {
              if (client.pipeline.carteBlanche) {
                return <span className="text-xs text-gray-400 italic">{t('compare_na', { defaultValue: 'N/A' })}</span>;
              }
              const c = client.pipeline.awaitingApproval ?? 0;
              const color = c === 0 ? 'text-primary-600' : c <= 3 ? 'text-coral-700' : 'text-coral-600';
              return (
                <>
                  <span className={`text-sm font-bold tabular-nums ${color}`}>{c}</span>
                  <DeltaBadge type={getDeltaType(pendDelta, client.id)} />
                </>
              );
            }}
          />

          {!velocityLocked && (
          <>
          {/* ── SECTION: Velocity ── */}
          <DesktopSectionHeader
            label={t('compare_section_velocity', { defaultValue: 'Velocity' })}
            tooltipKey="compare_tooltip_section_velocity"
            tooltipDefault="Posts published this week and trend vs prior week. Higher and trending up is best."
          />

          <div className={`hidden sm:grid ${gridCols} gap-0 border-b border-gray-100`}>
            {clients.map((client) => (
              <div key={client.id} className="px-5 py-5 border-r border-gray-100 last:border-r-0 flex items-center justify-center">
                <VelocityDisplay
                  thisWeek={client.velocity?.postsThisWeek ?? 0}
                  trendPct={client.velocity?.trendPct ?? 0}
                  deltaType={getDeltaType(velDelta, client.id)}
                />
              </div>
            ))}
          </div>

          <MobileSection
            label={t('compare_section_velocity', { defaultValue: 'Velocity' })}
            clients={clients}
            render={(client) => {
              const w = client.velocity?.postsThisWeek ?? 0;
              const trend = client.velocity?.trendPct ?? 0;
              const trendColor = trend > 0 ? 'text-primary-600' : trend < 0 ? 'text-coral-600' : 'text-gray-400';
              const trendText = trendLabel(t, trend);
              return (
                <>
                  <span className="text-sm font-bold tabular-nums text-gray-800">{w}{t('compare_per_week', { defaultValue: '/wk' })}</span>
                  <span className={`text-[11px] font-medium tabular-nums ${trendColor}`}>{trendText}</span>
                  <DeltaBadge type={getDeltaType(velDelta, client.id)} />
                </>
              );
            }}
          />
          </>
          )}

          {!cadenceLocked && (
          <>
          {/* ── SECTION: Cadence vs target ── */}
          <DesktopSectionHeader
            label={t('compare_section_cadence', { defaultValue: 'Cadence vs target' })}
            tooltipKey="compare_tooltip_section_cadence"
            tooltipDefault="Posts published this week vs the team's weekly target. 100% means on cadence."
          />

          <div className={`hidden sm:grid ${gridCols} gap-0 border-b border-gray-100`}>
            {clients.map((client) => (
              <div key={client.id} className="px-5 py-5 border-r border-gray-100 last:border-r-0 flex items-center justify-center">
                <CadenceDisplay
                  thisWeek={client.velocity?.postsThisWeek ?? 0}
                  target={client.cadenceWeekly}
                  deltaType={getDeltaType(cadDelta, client.id)}
                />
              </div>
            ))}
          </div>

          <MobileSection
            label={t('compare_section_cadence', { defaultValue: 'Cadence' })}
            clients={clients}
            render={(client) => {
              const w = client.velocity?.postsThisWeek ?? 0;
              const target = client.cadenceWeekly;
              if (target <= 0) return <span className="text-xs text-gray-400 italic">{t('compare_no_target', { defaultValue: 'No target' })}</span>;
              const pct = Math.round((w / target) * 100);
              const color = pct >= 100 ? 'text-primary-600' : pct >= 70 ? 'text-primary-700' : pct >= 40 ? 'text-coral-700' : 'text-coral-600';
              return (
                <>
                  <span className={`text-sm font-bold tabular-nums ${color}`}>
                    {w}/{target}
                  </span>
                  <span className="text-[11px] tabular-nums text-gray-500">{pct}%</span>
                  <DeltaBadge type={getDeltaType(cadDelta, client.id)} />
                </>
              );
            }}
          />
          </>
          )}

          {!relationshipLocked && (
          <>
          {/* ── SECTION: Renewal ── */}
          <DesktopSectionHeader
            label={t('compare_section_renewal', { defaultValue: 'Renewal' })}
            tooltipKey="compare_tooltip_section_renewal"
            tooltipDefault="Days until contract renewal. At-risk = renewal soon and signals are weak."
          />

          <div className={`hidden sm:grid ${gridCols} gap-0 border-b border-gray-100`}>
            {clients.map((client) => (
              <div key={client.id} className="px-5 py-5 border-r border-gray-100 last:border-r-0 flex items-center justify-center">
                <RenewalDisplay
                  daysUntilRenewal={client.renewal?.daysUntilRenewal ?? null}
                  atRisk={client.renewal?.atRisk ?? false}
                  deltaType={getDeltaType(renDelta, client.id)}
                />
              </div>
            ))}
          </div>

          <MobileSection
            label={t('compare_section_renewal', { defaultValue: 'Renewal' })}
            clients={clients}
            render={(client) => {
              const days = client.renewal?.daysUntilRenewal ?? null;
              const atRisk = client.renewal?.atRisk ?? false;
              if (days === null) return <span className="text-xs text-gray-400 italic">—</span>;
              const text = days <= 0
                ? t('compare_overdue', { n: Math.abs(days), defaultValue: '{{n}}d overdue' })
                : t('compare_days', { n: days, defaultValue: '{{n}}d' });
              const color = atRisk || days <= 0 ? 'text-coral-600' : days <= 30 ? 'text-coral-700' : 'text-primary-600';
              return (
                <>
                  <span className={`text-sm font-bold tabular-nums ${color}`}>{text}</span>
                  {atRisk && (
                    <span className="text-[10px] uppercase tracking-wide text-coral-600 font-bold">{tierLabel(t, 'at_risk')}</span>
                  )}
                  <DeltaBadge type={getDeltaType(renDelta, client.id)} />
                </>
              );
            }}
          />
          </>
          )}

          {!channelHealthLocked && (
          <>
          {/* ── SECTION: Channel health ── */}
          <DesktopSectionHeader
            label={t('compare_section_channels', { defaultValue: 'Channel health' })}
            tooltipKey="compare_tooltip_section_channels"
            tooltipDefault="Connected social channels — broken (re-auth needed) or expiring soon."
          />

          <div className={`hidden sm:grid ${gridCols} gap-0 border-b border-gray-100`}>
            {clients.map((client) => (
              <div key={client.id} className="px-5 py-5 border-r border-gray-100 last:border-r-0 flex items-center justify-center">
                <ChannelHealthDisplay
                  total={client.channelHealth?.total ?? 0}
                  broken={client.channelHealth?.broken ?? 0}
                  expiring={client.channelHealth?.expiring ?? 0}
                  deltaType={getDeltaType(chDelta, client.id)}
                />
              </div>
            ))}
          </div>

          <MobileSection
            label={t('compare_section_channels', { defaultValue: 'Channels' })}
            clients={clients}
            render={(client) => {
              const total = client.channelHealth?.total ?? 0;
              const broken = client.channelHealth?.broken ?? 0;
              const expiring = client.channelHealth?.expiring ?? 0;
              const issues = broken + expiring;
              return (
                <>
                  <span className="text-sm font-bold tabular-nums text-gray-800">{total}</span>
                  {issues > 0 ? (
                    <span className="text-[11px] font-medium text-coral-600">
                      {broken > 0 && t('compare_broken', { n: broken, defaultValue: '{{n}} broken' })}
                      {broken > 0 && expiring > 0 && ' · '}
                      {expiring > 0 && t('compare_expiring_short', { n: expiring, defaultValue: '{{n}} exp.' })}
                    </span>
                  ) : (
                    <span className="text-[11px] font-medium text-primary-600">{t('compare_all_healthy', { defaultValue: 'All healthy' })}</span>
                  )}
                  <DeltaBadge type={getDeltaType(chDelta, client.id)} />
                </>
              );
            }}
          />
          </>
          )}

          {/* ── SECTION: Brand lead ── */}
          <DesktopSectionHeader
            label={t('compare_section_owner', { defaultValue: 'Brand lead' })}
            tooltipKey="compare_tooltip_section_owner"
            tooltipDefault="Portfolio staff member assigned to this brand. Useful when comparing workload."
          />

          <div className={`hidden sm:grid ${gridCols} gap-0 border-b border-gray-100`}>
            {clients.map((client) => (
              <div key={client.id} className="px-5 py-4 border-r border-gray-100 last:border-r-0 flex items-center justify-center">
                <OwnerChip
                  initials={client.ownerInitials}
                  name={client.ownerName}
                  color={client.ownerAvatarColor}
                  profilePhotoUrl={client.ownerProfilePhotoUrl}
                />
              </div>
            ))}
          </div>

          <MobileSection
            label={t('compare_section_owner', { defaultValue: 'Brand lead' })}
            clients={clients}
            render={(client) => (
              <OwnerChip
                initials={client.ownerInitials}
                name={client.ownerName}
                color={client.ownerAvatarColor}
                profilePhotoUrl={client.ownerProfilePhotoUrl}
              />
            )}
          />

          {/* ── SECTION: Latest Status ── */}
          <DesktopSectionHeader
            label={t('compare_section_status', { defaultValue: 'Latest Status Update' })}
            tooltipKey="compare_tooltip_section_status"
            tooltipDefault="Most recent agency status update - the pulse of this client relationship."
          />

          <div className={`hidden sm:grid ${gridCols} gap-0 border-b border-gray-100`}>
            {clients.map((client) => (
              <div key={client.id} className="px-5 py-4 border-r border-gray-100 last:border-r-0">
                {client.latestStatusUpdate ? (
                  <div className="flex items-start gap-2">
                    <span className="text-xl leading-none shrink-0 mt-0.5" aria-hidden="true">
                      {client.latestStatusUpdate.vibe}
                    </span>
                    <div className="min-w-0">
                      <p className="text-sm text-gray-700 leading-snug">
                        {client.latestStatusUpdate.tldr}
                      </p>
                      <p className="text-xs text-gray-400 mt-1.5">
                        {client.latestStatusUpdate.authorName} ·{' '}
                        {formatRelativeDate(client.latestStatusUpdate.createdAt)}
                      </p>
                    </div>
                  </div>
                ) : (
                  <span className="text-xs text-gray-300 italic">
                    {t('portfolio_status_no_update', { defaultValue: 'No update' })}
                  </span>
                )}
              </div>
            ))}
          </div>

          {/* Mobile status — multi-line, bespoke (TL;DR wraps under client name) */}
          <section className="sm:hidden border-b border-gray-100">
            <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-[0.15em] px-4 pt-3.5 pb-2">
              {t('compare_section_status', { defaultValue: 'Latest Status Update' })}
            </p>
            <div className="px-4 pb-3.5 divide-y divide-gray-50">
              {clients.map((client) => (
                <div key={client.id} className="flex items-start gap-2.5 py-2.5 first:pt-0 last:pb-0">
                  <div
                    className={`w-7 h-7 rounded-md flex items-center justify-center text-[11px] font-bold ${avatarTextClass(client.avatarColor)} shrink-0 mt-0.5 ${client.avatarColor}`}
                    aria-hidden="true"
                  >
                    {client.avatarInitial}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate leading-tight">{client.name}</p>
                    {client.latestStatusUpdate ? (
                      <>
                        <div className="flex items-start gap-1.5 mt-1">
                          <span className="text-base leading-none shrink-0" aria-hidden="true">
                            {client.latestStatusUpdate.vibe}
                          </span>
                          <p className="text-xs text-gray-600 leading-snug line-clamp-2">
                            {client.latestStatusUpdate.tldr}
                          </p>
                        </div>
                        <p className="text-[10px] text-gray-400 mt-1">
                          {formatRelativeDate(client.latestStatusUpdate.createdAt)}
                        </p>
                      </>
                    ) : (
                      <p className="text-xs text-gray-300 italic mt-0.5">
                        {t('portfolio_status_no_update', { defaultValue: 'No update' })}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* ── SECTION: Lifecycle ── */}
          <DesktopSectionHeader
            label={t('compare_section_lifecycle', { defaultValue: 'Lifecycle Stage' })}
            tooltipKey="compare_tooltip_section_lifecycle"
            tooltipDefault="Where the client relationship stands: onboarding, active, churning, or paused."
          />

          <div className={`hidden sm:grid ${gridCols} gap-0 pb-6`}>
            {clients.map((client) => {
              const lifecycle = LIFECYCLE_CONFIG[client.lifecycleStage];
              return (
                <div key={client.id} className="px-5 py-4 border-r border-gray-100 last:border-r-0 flex items-center justify-center">
                  <span className={`text-sm px-3 py-1 rounded-full font-semibold ${lifecycle.bg} ${lifecycle.text}`}>
                    {lifecycleStageLabel(t, client.lifecycleStage)}
                  </span>
                </div>
              );
            })}
          </div>

          <MobileSection
            label={t('compare_section_lifecycle', { defaultValue: 'Lifecycle' })}
            clients={clients}
            className="pb-6 border-b-0"
            render={(client) => {
              const lifecycle = LIFECYCLE_CONFIG[client.lifecycleStage];
              return (
                <span
                  className={`text-xs px-2 py-0.5 rounded-full font-semibold leading-none ${lifecycle.bg} ${lifecycle.text}`}
                >
                  {lifecycleStageLabel(t, client.lifecycleStage)}
                </span>
              );
            }}
          />

        </div>
        {/* end scrollable body */}
      </div>
    </div>
  );
};

export default ComparePanel;
